import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Plus, Minus } from "lucide-react";
import { Link } from "react-router-dom";

const FAQ_GROUPS = [
  {
    group: "Grants & eligibility",
    items: [
      { q: "Who is GrantFlow for?", a: "US-based LLCs, nonprofits (501(c)(3)), sole proprietors, and corporations that want to apply for federal, state, and private grants without hiring an expensive grant consultant." },
      { q: "Are the grants in the database real?", a: "Yes. Every grant is a real, active US grant program from federal agencies (SBA, NIH, USDA, NSF, DOE, EPA, NEA, NEH, MBDA), state economic development offices, corporate foundations (FedEx, Wells Fargo, Chase, Patagonia), and private foundations (MacArthur, Carnegie, Rockefeller, Pew). Deadlines are approximate — always verify on the funder's site before submitting." },
      { q: "Do you guarantee I'll win a grant?", a: "No — no one legitimately can. But grant reviewers score applications on document quality and completeness, and that's exactly what we optimize. Members typically apply to 5-10x more grants than they did before joining." },
      { q: "Can I apply as an individual or unregistered entity?", a: "Most grants require an EIN and a legal entity (LLC, 501(c)(3), etc). Our Academy walks you through the exact steps to register — takes about 30 minutes." },
    ],
  },
  {
    group: "AI documents",
    items: [
      { q: "What documents can the AI draft?", a: "8 types: Business Plan, Cover Letter, Project Narrative, Budget Justification, Executive Summary, Mission Statement, Organizational Chart, and Sustainability Plan. Every draft is grounded in your organization profile and the specific grant you're chasing." },
      { q: "What AI model do you use?", a: "Claude Sonnet 5 by Anthropic — the highest-quality model for long-form business writing available today." },
      { q: "Can I edit the AI drafts?", a: "Yes. Drafts download as Markdown files you can open in Word, Google Docs, or Notion. Copy/paste, edit freely — the AI does the heavy lift, you polish the details." },
      { q: "Is the AI making up facts?", a: "The AI uses ONLY the organization profile you provide plus the grant description. It never invents EINs, tax IDs, or legal claims — those show up as `[EIN]` placeholders for you to fill in." },
    ],
  },
  {
    group: "Billing & refunds",
    items: [
      { q: "How much does it cost?", a: "$100/month or $960/year (save $240). Free to browse grants — subscription unlocks AI document generation, application tracker, and Academy." },
      { q: "Can I cancel anytime?", a: "Yes. Cancel from your Stripe billing portal or by emailing support. You keep access until the end of your current billing period." },
      { q: "Do you offer refunds?", a: "Yes — 14-day money-back guarantee, no questions asked. See our full refund policy for details." },
      { q: "Do you offer discounts for nonprofits?", a: "The annual plan is already 20% off. For 501(c)(3) organizations under $250K annual revenue, email support@grantflow.app for an additional 30% off." },
    ],
  },
  {
    group: "Security & privacy",
    items: [
      { q: "Is my organization data secure?", a: "Yes. All data encrypted in transit (TLS 1.3) and at rest. Passwords hashed with bcrypt (12 rounds). Auth endpoints rate-limited. Security headers (HSTS, X-Frame-Options, etc.) enforced. No org data is ever sold or shared." },
      { q: "Does the AI train on my data?", a: "No. We use Claude via a paid API — Anthropic does not train on API traffic. Your org details stay yours." },
      { q: "Where is data stored?", a: "MongoDB Atlas in US-East. GDPR / CCPA delete requests honored within 30 days — email support with your account email." },
    ],
  },
];

function FaqItem({ q, a, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-neutral-200" data-testid={testId}>
      <button onClick={() => setOpen(!open)}
        className="w-full py-5 flex items-center justify-between text-left gap-4 hover:text-neutral-900 transition-colors">
        <span className="font-medium">{q}</span>
        <span className="shrink-0 h-8 w-8 rounded-full border border-neutral-200 flex items-center justify-center">
          {open ? <Minus size={14} /> : <Plus size={14} />}
        </span>
      </button>
      {open && <div className="pb-5 pr-10 text-neutral-600 leading-relaxed">{a}</div>}
    </div>
  );
}

export default function FAQ() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-14" data-testid="faq-page">
        <div className="overline mb-3">FAQ</div>
        <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight" data-testid="faq-title">Everything most members ask on day one.</h1>
        <p className="text-neutral-600 mt-4 max-w-2xl">Didn't see your question? <Link to="/contact" className="underline">Message the team</Link> and a human will reply within one business day.</p>

        <div className="mt-14 space-y-14">
          {FAQ_GROUPS.map((g, gi) => (
            <div key={gi}>
              <h2 className="font-display text-2xl font-semibold mb-2" data-testid={`faq-group-${gi}`}>{g.group}</h2>
              <div>
                {g.items.map((it, i) => (
                  <FaqItem key={i} q={it.q} a={it.a} testId={`faq-item-${gi}-${i}`} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
